import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, Ruler, Layers, ArrowDownAZ, ChevronsDownUp, ChevronsUpDown,
} from "lucide-react";
import api from "@/lib/api";
import LoadingScreen from "@/components/LoadingScreen";
import WorkAreaModal from "@/components/WorkAreaModal";
import PrefCell from "@/components/PrefCell";
import MeasureCell from "@/components/MeasureCell";
import Highlight from "@/components/Highlight";
import ListSearch from "@/components/ListSearch";
import { matchesQuery, buildHaystack } from "@/lib/search";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PLACEMENT_META = {
  suelo: { label: "Suelos", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  pared: { label: "Paredes", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  techo: { label: "Techos", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  espacio: { label: "Espacios", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  organizacion: { label: "Organización", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const byPreference = (a, b) => {
  const pa = a.preference || "", pb = b.preference || "";
  if (pa && pb) {
    const c = pa.localeCompare(pb, "es", { numeric: true, sensitivity: "base" });
    if (c !== 0) return c;
  } else if (pa && !pb) return -1;
  else if (!pa && pb) return 1;
  return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
};

const PLACEMENT_ORDER = ["suelo", "pared", "techo", "espacio", "organizacion"];

// Agrupa por Espacio (placement) respetando el orden definido; las áreas sin espacio van al final.
const groupByEspacio = (list) => {
  const m = {};
  for (const a of list) {
    const k = a.placement || "none";
    (m[k] = m[k] || []).push(a);
  }
  const keys = [...PLACEMENT_ORDER.filter((k) => m[k]), ...(m.none ? ["none"] : [])];
  return keys.map((k) => [k, m[k].slice().sort(byPreference)]);
};


export default function ModelWorkAreas({ initialQuery = "" }) {
  const [levels, setLevels] = useState(null);
  const [areas, setAreas] = useState([]);
  const [units, setUnits] = useState([]);
  const [view, setView] = useState("levels"); // "levels" | "alpha"
  const [search, setSearch] = useState(initialQuery || "");
  const [placementFilter, setPlacementFilter] = useState("");
  const [openLevels, setOpenLevels] = useState(null); // null = aún sin inicializar
  const [areaModalOpen, setAreaModalOpen] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [defaultLevelId, setDefaultLevelId] = useState(null);
  const [areaToDelete, setAreaToDelete] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/model/levels"),
      api.get("/model/work-areas"),
      api.get("/resource-units"),
    ]).then(([l, a, u]) => { setLevels(l.data); setAreas(a.data); setUnits(u.data); })
      .catch(() => toast.error("No se pudo cargar la vivienda modelo."));
  }, []);

  const areasByLevel = useMemo(() => {
    const map = {};
    for (const a of areas) (map[a.level_id] = map[a.level_id] || []).push(a);
    return map;
  }, [areas]);

  const levelName = useMemo(() => {
    const m = {};
    for (const l of (levels || [])) m[l.level_id] = l.name;
    return m;
  }, [levels]);

  // Niveles que SÍ tienen áreas, en el orden definido de los niveles.
  const levelsWithAreas = useMemo(
    () => (levels || []).filter((l) => (areasByLevel[l.level_id] || []).length > 0),
    [levels, areasByLevel]
  );

  const alphaAreas = useMemo(
    () => [...areas].sort((a, b) => a.name.localeCompare(b.name, "es", { sensitivity: "base" })),
    [areas]
  );

  const passArea = (a) => (!placementFilter || a.placement === placementFilter) && matchesQuery(search, buildHaystack(a, levelName[a.level_id], PLACEMENT_META[a.placement]?.label));
  const displayLevelIds = useMemo(
    () => levelsWithAreas
      .filter((l) => (areasByLevel[l.level_id] || []).some((a) => (!placementFilter || a.placement === placementFilter) && matchesQuery(search, buildHaystack(a, levelName[a.level_id], PLACEMENT_META[a.placement]?.label))))
      .map((l) => l.level_id),
    [levelsWithAreas, areasByLevel, levelName, search, placementFilter]
  );
  const alphaFiltered = useMemo(() => alphaAreas.filter((a) => (!placementFilter || a.placement === placementFilter) && matchesQuery(search, buildHaystack(a, levelName[a.level_id], PLACEMENT_META[a.placement]?.label))), [alphaAreas, levelName, search, placementFilter]);

  // Abre todos los niveles con áreas la primera vez que hay datos.
  useEffect(() => {
    if (openLevels === null && levels) setOpenLevels(levelsWithAreas.map((l) => l.level_id));
  }, [openLevels, levels, levelsWithAreas]);

  const openNewArea = (levelId) => { setEditingArea(null); setDefaultLevelId(levelId || null); setAreaModalOpen(true); };
  const openEditArea = (a) => { setEditingArea(a); setDefaultLevelId(null); setAreaModalOpen(true); };

  const onAreaSaved = (saved, wasEditing, unit) => {
    setAreas((list) => (wasEditing ? list.map((a) => (a.area_id === saved.area_id ? saved : a)) : [...list, saved]));
    if (unit && !units.includes(unit)) setUnits((u) => [...u, unit]);
    setOpenLevels((o) => (o && !o.includes(saved.level_id) ? [...o, saved.level_id] : o));
  };

  const updatePlacement = async (area, value) => {
    if (!value || value === area.placement) return;
    const prev = area.placement;
    setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, placement: value } : a)));
    try {
      await api.put(`/model/work-areas/${area.area_id}`, { placement: value });
    } catch (e) {
      setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, placement: prev } : a)));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el espacio.");
    }
  };

  const updateMeasure = async (area, value) => {
    const prev = area.measure ?? null;
    if (value === prev) return;
    setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, measure: value } : a)));
    try {
      await api.put(`/model/work-areas/${area.area_id}`, { measure: value });
    } catch (e) {
      setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, measure: prev } : a)));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la medición.");
    }
  };

  const updatePreference = async (area, value) => {
    if ((value || "") === (area.preference || "")) return;
    const prev = area.preference || "";
    setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, preference: value } : a)));
    try {
      await api.put(`/model/work-areas/${area.area_id}`, { preference: value });
    } catch (e) {
      setAreas((list) => list.map((a) => (a.area_id === area.area_id ? { ...a, preference: prev } : a)));
      toast.error(e?.response?.data?.detail || "No se pudo actualizar la preferencia.");
    }
  };

  const confirmDeleteArea = async () => {
    if (!areaToDelete) return;
    try {
      await api.delete(`/model/work-areas/${areaToDelete.area_id}`);
      setAreas((list) => list.filter((a) => a.area_id !== areaToDelete.area_id));
      toast.success("Área eliminada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el área.");
    } finally {
      setAreaToDelete(null);
    }
  };

  const expandAll = () => setOpenLevels(levelsWithAreas.map((l) => l.level_id));
  const collapseAll = () => setOpenLevels([]);

  const renderArea = (a, showLevel, editMeasure) => (
    <div key={a.area_id} data-testid={`area-row-${a.area_id}`}
      className="group flex items-start gap-3 rounded-xl border border-border bg-background/60 p-3 hover:bg-background-subtle transition-colors">
      <PrefCell value={a.preference} onSave={(v) => updatePreference(a, v)} testId={`area-pref-input-${a.area_id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate"><Highlight text={a.name} query={search} /></span>
          <Select value={a.placement || undefined} onValueChange={(v) => updatePlacement(a, v)}>
            <SelectTrigger
              className={`h-6 w-auto shrink-0 gap-1 rounded-full border px-2 py-0 text-[10px] font-medium ${PLACEMENT_META[a.placement]?.cls || "text-muted-foreground"}`}
              data-testid={`area-placement-select-${a.area_id}`}
            >
              <SelectValue placeholder="Espacio" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PLACEMENT_META).map(([val, meta]) => (
                <SelectItem key={val} value={val} data-testid={`area-placement-opt-${a.area_id}-${val}`}>{meta.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {showLevel && (
            <Badge variant="secondary" className="font-normal text-[10px] gap-1 shrink-0" data-testid={`area-level-badge-${a.area_id}`}>
              <Layers className="h-3 w-3" /> {levelName[a.level_id] || "—"}
            </Badge>
          )}
          {editMeasure ? (
            <MeasureCell value={a.measure} unit={a.unit} onSave={(v) => updateMeasure(a, v)} testId={`area-measure-input-${a.area_id}`} />
          ) : (a.measure !== null && a.measure !== undefined && a.measure !== "") ? (
            <Badge variant="outline" className="font-mono font-normal text-[10px] gap-1 shrink-0">
              <Ruler className="h-3 w-3" /> {a.measure}{a.unit ? ` ${a.unit}` : ""}
            </Badge>
          ) : null}
        </div>
        {a.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{a.description}</p>}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditArea(a)} data-testid={`edit-area-${a.area_id}`} aria-label="Editar">
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setAreaToDelete(a)} data-testid={`delete-area-${a.area_id}`} aria-label="Eliminar">
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );

  if (!levels) return <LoadingScreen text="Cargando áreas de trabajo…" />;

  const wq = search.trim();
  const filtering = Boolean(wq) || Boolean(placementFilter);

  const toggleBtn = (key, label, Icon) => (
    <button
      type="button"
      onClick={() => setView(key)}
      data-testid={`view-${key}`}
      className={`inline-flex items-center gap-1.5 rounded-full px-3.5 h-8 text-sm font-medium transition-colors ${
        view === key ? "bg-[#C75B28] text-white" : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div data-testid="model-work-areas">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Áreas de trabajo</h2>
          <p className="text-sm text-muted-foreground mt-1">Consúltalas agrupadas por nivel o en orden alfabético.</p>
        </div>
        <Button className="rounded-full gap-2" onClick={() => openNewArea(null)} data-testid="new-work-area-button">
          <Plus className="h-4 w-4" /> Nueva área de trabajo
        </Button>
      </div>

      <div className="mb-4">
        <ListSearch value={search} onChange={setSearch} placeholder="Buscar por cualquier contenido…" testId="work-area-search-input" />
      </div>

      {areas.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="work-area-placement-filter">
          <span className="text-xs text-muted-foreground mr-1">Espacio:</span>
          <button type="button" onClick={() => setPlacementFilter("")} data-testid="work-area-placement-filter-all"
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${!placementFilter ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}>Todos</button>
          {Object.entries(PLACEMENT_META).map(([val, meta]) => (
            <button type="button" key={val} onClick={() => setPlacementFilter(placementFilter === val ? "" : val)} data-testid={`work-area-placement-filter-${val}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${placementFilter === val ? meta.cls : "bg-card text-muted-foreground border-border hover:bg-secondary"}`}>{meta.label}</button>
          ))}
        </div>
      )}

      {/* Barra de vista */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex rounded-full border border-border bg-card p-1" data-testid="work-areas-view-toggle">
          {toggleBtn("levels", "Por niveles", Layers)}
          {toggleBtn("alpha", "Alfabético", ArrowDownAZ)}
        </div>
        {view === "levels" && levelsWithAreas.length > 0 && (
          <div className="flex items-center gap-1.5">
            <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-8" onClick={expandAll} data-testid="expand-all-levels">
              <ChevronsUpDown className="h-4 w-4" /> Expandir todo
            </Button>
            <Button variant="outline" size="sm" className="rounded-full gap-1.5 h-8" onClick={collapseAll} data-testid="collapse-all-levels">
              <ChevronsDownUp className="h-4 w-4" /> Comprimir todo
            </Button>
          </div>
        )}
      </div>

      {areas.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="work-areas-empty">
          <Layers className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Aún no hay áreas de trabajo. Crea la primera para empezar.</p>
          <Button className="rounded-full gap-2 mt-4" onClick={() => openNewArea(null)} data-testid="empty-new-area-button">
            <Plus className="h-4 w-4" /> Nueva área de trabajo
          </Button>
        </div>
      ) : view === "levels" ? (
        <>
        {filtering && displayLevelIds.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6" data-testid="work-areas-no-match">Ningún área coincide con el filtro.</p>
        )}
        <Accordion
          type="multiple"
          value={filtering ? displayLevelIds : (openLevels || [])}
          onValueChange={(v) => { if (!filtering) setOpenLevels(v); }}
          className="space-y-3"
          data-testid="work-areas-accordion"
        >
          {levelsWithAreas.map((l) => {
            const list = (areasByLevel[l.level_id] || []).filter(passArea).sort(byPreference);
            if (list.length === 0) return null;
            return (
              <AccordionItem key={l.level_id} value={l.level_id} className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`level-group-${l.level_id}`}>
                <AccordionTrigger className="px-5 py-4 hover:no-underline">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <Layers className="h-4 w-4 text-primary shrink-0" />
                    <span className="text-base font-semibold tracking-tight truncate">{l.name}</span>
                    <Badge variant="secondary" className="font-normal shrink-0">{list.length} {list.length === 1 ? "área" : "áreas"}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-5">
                  <div className="space-y-4">
                    {groupByEspacio(list).map(([pk, items]) => (
                      <div key={pk} data-testid={`level-${l.level_id}-espacio-${pk}`}>
                        <div className="flex items-center gap-2 mb-2 pl-0.5">
                          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${PLACEMENT_META[pk]?.cls || "text-muted-foreground border-border bg-secondary"}`}>
                            {PLACEMENT_META[pk]?.label || "Sin espacio"}
                          </span>
                          <span className="text-[11px] font-medium text-muted-foreground">{items.length}</span>
                          <span className="flex-1 border-t border-border/60" />
                        </div>
                        <div className="space-y-2">
                          {items.map((a) => renderArea(a, false, false))}
                        </div>
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" className="rounded-full gap-1.5 text-primary hover:text-primary mt-1" onClick={() => openNewArea(l.level_id)} data-testid={`add-area-in-${l.level_id}`}>
                      <Plus className="h-4 w-4" /> Añadir área a este nivel
                    </Button>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
        </>
      ) : (
        <div className="rounded-2xl border border-border bg-card p-4 space-y-2" data-testid="work-areas-alpha-list">
          {alphaFiltered.map((a) => renderArea(a, true, true))}
          {alphaFiltered.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6" data-testid="work-areas-no-match">Ningún área coincide con el filtro.</p>
          )}
        </div>
      )}

      <WorkAreaModal open={areaModalOpen} onOpenChange={setAreaModalOpen} levels={levels} units={units}
        defaultLevelId={defaultLevelId} area={editingArea} onSaved={onAreaSaved} />

      <AlertDialog open={Boolean(areaToDelete)} onOpenChange={(v) => { if (!v) setAreaToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{areaToDelete?.name}»?</AlertDialogTitle>
            <AlertDialogDescription>Se quitará de la vivienda modelo. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction className="rounded-full bg-destructive hover:bg-destructive/90" onClick={confirmDeleteArea} data-testid="confirm-delete-area-button">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
